<script setup lang="ts">
import { useBillingUtils } from '@/composables/useBillingUtils';
import { useI18n } from '@/i18n';
import { formatPeriodHeader, groupLinesByPeriod, simplifyLineDescription } from '@/lib/invoiceFormat';
import { useBillingStore } from '@/stores/billing';
import { Alert, Button, Divider, Heading, LayoutGap, Loader, Prose } from '@dabble/quillui';
import { format } from 'date-fns';
import type { Stripe } from 'stripe';
import { computed } from 'vue';

const props = defineProps<{
  upcomingInvoice: Stripe.Invoice | null;
  recurringInvoice: Stripe.Invoice | null;
  isLoadingInvoice: boolean;
  planName: string;
  planAmount: number;
  planCurrency: string;
  planInterval: string;
  noPaymentRequired?: boolean;
  subscribeDisabled: boolean;
  subscribeLoading: boolean;
}>();

const emit = defineEmits<{ subscribe: [] }>();

const { t } = useI18n();
const { formatPrice, couponAmount, couponDuration } = useBillingUtils();
const billingStore = useBillingStore();

const intervalLabel = computed(() => t(`checkout_interval_${props.planInterval}_subscription`));

const isChangingPlan = computed(() => billingStore.isActivePaid);

const invoiceDiscount = computed<Stripe.Discount | null>(() => {
  const inv = props.upcomingInvoice;
  if (!inv) return null;
  const d = inv.discounts?.[0];
  if (!d || typeof d === 'string') return null;
  return d as Stripe.Discount;
});

const discountCoupon = computed<Stripe.Coupon | null>(() => {
  const d = invoiceDiscount.value as Record<string, unknown> | null;
  if (!d) return null;
  const direct = d.coupon;
  if (direct && typeof direct === 'object') return direct as Stripe.Coupon;
  const src = d.source as Record<string, unknown> | undefined;
  const nested = src?.coupon;
  if (nested && typeof nested === 'object') return nested as Stripe.Coupon;
  return null;
});

const discountPromotionCode = computed<string | null>(() => {
  const pc = invoiceDiscount.value?.promotion_code;
  if (!pc) return null;
  return typeof pc === 'string' ? pc : pc.code;
});

const invoiceCustomer = computed<Stripe.Customer | null>(() => {
  const raw = props.upcomingInvoice?.customer;
  if (!raw || typeof raw === 'string') return null;
  return raw as Stripe.Customer;
});

const appliedBalance = computed(() => {
  const inv = props.upcomingInvoice;
  const cust = invoiceCustomer.value;
  if (!inv || !cust?.balance || !inv.total) return null;
  return Math.max(-inv.total, cust.balance);
});

// Trial end date for this checkout. Order of preference:
//   1. recurringInvoice's first line.period.start — most accurate; comes from
//      Stripe's "first paid invoice" preview when one was returned.
//   2. an active trialing subscription's trial_end.
//   3. heuristic fallback: trial-preview invoice (no sub yet, $0 due against a
//      non-zero plan; the preview line carries the trial period.end).
const trialEndSeconds = computed<number | null>(() => {
  const recurringStart = props.recurringInvoice?.lines?.data?.[0]?.period?.start;
  if (recurringStart) return recurringStart;
  const sub = billingStore.primarySubscription;
  if (sub?.status === 'trialing' && sub.trial_end) return sub.trial_end;
  const inv = props.upcomingInvoice;
  if (inv && !sub && inv.amount_due === 0 && props.planAmount > 0) {
    return inv.lines?.data?.[0]?.period?.end ?? null;
  }
  return null;
});

const trialEndDate = computed(() =>
  trialEndSeconds.value ? format(new Date(trialEndSeconds.value * 1000), 'PP') : '',
);

const amountDueLabel = computed(() => {
  const inv = props.upcomingInvoice;
  if (!inv) return '';
  if (inv.amount_due < 0) return t('checkout_applied_to_your_account');
  if (inv.next_payment_attempt && billingStore.primarySubscription && !trialEndDate.value) {
    return t('checkout_amount_due_on_date', {
      date: format(new Date(inv.next_payment_attempt * 1000), 'PP'),
    });
  }
  return t('checkout_amount_due_today');
});

const linesByPeriod = computed(() => groupLinesByPeriod(props.upcomingInvoice));
const recurringLinesByPeriod = computed(() => groupLinesByPeriod(props.recurringInvoice));

// Date the first real (recurring) charge will hit — first day of the recurring
// invoice's period, which is also when the trial ends.
const recurringStartDate = computed(() => {
  const start = props.recurringInvoice?.lines?.data?.[0]?.period?.start;
  return start ? format(new Date(start * 1000), 'PP') : '';
});
</script>

<template>
  <LayoutGap direction="column" size="small" fill class="invoice-summary">
    <LayoutGap direction="column" size="xxsmall" fill>
      <Heading :level="2" no-margin>{{ t('checkout_order_summary') }}</Heading>
      <Divider variant="secondary" size="tiny" />
    </LayoutGap>

    <LayoutGap
      direction="row"
      align="space-between"
      vertical-align="top"
      size="small"
      fill
      class="invoice-summary__row invoice-summary__row--plan"
    >
      <LayoutGap direction="column" size="xxsmall" class="invoice-summary__plan-info">
        <strong>{{ planName }}</strong>
        <Prose variant="secondary" size="small">{{ intervalLabel }}</Prose>
      </LayoutGap>
      <strong>{{ formatPrice(planAmount, planCurrency) }}</strong>
    </LayoutGap>

    <Loader v-if="isLoadingInvoice && !upcomingInvoice" />

    <template v-else-if="upcomingInvoice">
      <div class="invoice-summary__details">
        <div v-for="group in linesByPeriod" :key="group.key" class="invoice-summary__period">
          <div class="invoice-summary__period-header">
            {{ formatPeriodHeader(group.start, group.end) }}
          </div>
          <div v-for="(line, idx) in group.lines" :key="line.id ?? idx" class="invoice-summary__row">
            <span class="invoice-summary__row-label">{{ simplifyLineDescription(line.description) }}</span>
            <span class="invoice-summary__row-value">{{ formatPrice(line.amount, upcomingInvoice.currency) }}</span>
          </div>
        </div>

        <div class="invoice-summary__row invoice-summary__row--emphasis">
          <span class="invoice-summary__row-label">{{ t('checkout_subtotal') }}</span>
          <span class="invoice-summary__row-value">{{
            formatPrice(upcomingInvoice.subtotal, upcomingInvoice.currency)
          }}</span>
        </div>

        <div v-if="discountCoupon" class="invoice-summary__row">
          <span class="invoice-summary__row-label">
            {{ discountCoupon.name || discountPromotionCode || discountCoupon.id.toUpperCase() }}
            ({{ couponAmount(discountCoupon, true) }} {{ couponDuration(discountCoupon) }})
          </span>
          <span class="invoice-summary__row-value">({{ couponAmount(discountCoupon) }})</span>
        </div>

        <div class="invoice-summary__row invoice-summary__row--emphasis">
          <span class="invoice-summary__row-label">{{ t('checkout_total') }}</span>
          <span class="invoice-summary__row-value">{{
            formatPrice(upcomingInvoice.total, upcomingInvoice.currency)
          }}</span>
        </div>
      </div>

      <Divider variant="secondary" size="tiny" />

      <div v-if="appliedBalance" class="invoice-summary__row">
        <span class="invoice-summary__row-label">{{ t('checkout_applied_balance') }}</span>
        <span class="invoice-summary__row-value">{{ formatPrice(appliedBalance, upcomingInvoice.currency) }}</span>
      </div>

      <div class="invoice-summary__row invoice-summary__row--emphasis">
        <span class="invoice-summary__row-label">{{ amountDueLabel }}</span>
        <span class="invoice-summary__row-value">{{
          formatPrice(upcomingInvoice.amount_due, upcomingInvoice.currency)
        }}</span>
      </div>
    </template>

    <template v-if="recurringInvoice">
      <div class="invoice-summary__details">
        <div v-for="group in recurringLinesByPeriod" :key="group.key" class="invoice-summary__period">
          <div class="invoice-summary__period-header">
            {{ formatPeriodHeader(group.start, group.end) }}
          </div>
          <div v-for="(line, idx) in group.lines" :key="line.id ?? idx" class="invoice-summary__row">
            <span class="invoice-summary__row-label">{{ simplifyLineDescription(line.description) }}</span>
            <span class="invoice-summary__row-value">{{ formatPrice(line.amount, recurringInvoice.currency) }}</span>
          </div>
        </div>

        <div class="invoice-summary__row invoice-summary__row--emphasis">
          <span class="invoice-summary__row-label">{{
            recurringStartDate ? t('checkout_amount_due_on_date', { date: recurringStartDate }) : t('checkout_total')
          }}</span>
          <span class="invoice-summary__row-value">{{
            formatPrice(recurringInvoice.total, recurringInvoice.currency)
          }}</span>
        </div>
      </div>
    </template>

    <Alert
      v-if="noPaymentRequired"
      variant="info"
      :title="t('checkout_no_payment_required_title')"
      :message="t('checkout_no_payment_required_message')"
      :no-icon="true"
    />

    <Button variant="primary" fill-width :disabled="subscribeDisabled" @click="emit('subscribe')">
      {{
        subscribeLoading
          ? t('checkout_processing')
          : isChangingPlan
            ? t('billing_change_plan')
            : t('checkout_subscribe_now')
      }}
    </Button>

    <div v-if="trialEndDate" class="invoice-summary__trial-info">
      {{ t('checkout_due_not_charged_until', { date: trialEndDate }) }}
    </div>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.invoice-summary {
  flex: 1 1 20rem;
  min-width: 0;

  &__plan-info {
    min-width: 0;
  }

  &__trial-info {
    text-align: center;
    font-size: 0.8125rem;
    color: var(--text-color-lightest, #6b7280);
    margin-top: 0.25rem;
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem 0;
    border-top: 1px solid var(--border-color, #e5e7eb);
    border-bottom: 1px solid var(--border-color, #e5e7eb);
  }

  &__period {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    & + & {
      margin-top: 0.5rem;
    }

    &-header {
      font-size: 0.6875rem;
      font-weight: 600;
      letter-spacing: 0.05em;
      color: var(--text-color-lightest, #6b7280);
      padding-bottom: 0.25rem;
      border-bottom: 1px solid var(--border-color, #e5e7eb);
      margin-bottom: 0.25rem;
    }
  }

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    font-size: 0.875rem;

    &-label {
      flex: 1;
      min-width: 0;
      color: var(--text-color-light, #4b5563);
    }

    &-value {
      text-align: right;
      white-space: nowrap;
    }

    &--plan {
      font-size: 1rem;
    }

    &--emphasis {
      font-weight: 600;
    }

    &--emphasis .invoice-summary__row-label,
    &--emphasis .invoice-summary__row-value {
      color: var(--text-color, #111827);
    }
  }
}
</style>
